/**
 * Google Analytics 4 - Minimal Integration
 *
 * Provides session metrics, device analytics, and user flow visualization
 * to complement the existing Supabase user activity tracking.
 *
 * This is intentionally minimal - we rely on GA4's enhanced measurement
 * for automatic page view tracking and don't duplicate events already
 * tracked in Supabase.
 */

import { env } from '@/config/environment';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Initialize GA4 with enhanced measurement enabled.
 * Call this once on app mount (in production only).
 */
export const initGA4 = (): void => {
  if (initialized) return;
  if (!env.ga4.enabled || !env.ga4.measurementId) {
    if (env.isDevelopment) {
      console.log('[GA4] Disabled or missing measurement ID');
    }
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${env.ga4.measurementId}`;
  document.head.appendChild(script);

  // Initialize GA4
  window.gtag('js', new Date());
  window.gtag('config', env.ga4.measurementId, {
    // Let enhanced measurement handle page views automatically
    send_page_view: true,
    // Enable debug mode if configured
    debug_mode: env.ga4.debugMode
  });

  initialized = true;

  if (env.isDevelopment || env.ga4.debugMode) {
    console.log('[GA4] Initialized with measurement ID:', env.ga4.measurementId);
  }
};

/**
 * Set user properties for segmentation in GA4 reports.
 * Call when user auth state or tenant changes.
 */
export const setUserProperties = (properties: {
  user_role?: string;
  tenant_id?: string;
  tenant_name?: string;
}): void => {
  if (!env.ga4.enabled || !window.gtag) return;

  window.gtag('set', 'user_properties', properties);

  if (env.ga4.debugMode) {
    console.log('[GA4] Set user properties:', properties);
  }
};

/**
 * Set hashed user ID for cross-session analysis.
 * Call on login, clear on logout by passing null.
 */
export const setUserId = (userId: string | null): void => {
  if (!env.ga4.enabled || !window.gtag || !env.ga4.measurementId) return;

  if (userId) {
    // Hash the user ID for privacy (simple base64, not cryptographic)
    const hashedId = btoa(userId).substring(0, 32);
    window.gtag('config', env.ga4.measurementId, {
      user_id: hashedId
    });

    if (env.ga4.debugMode) {
      console.log('[GA4] Set user ID (hashed)');
    }
  } else {
    // Clear user ID on logout
    window.gtag('config', env.ga4.measurementId, {
      user_id: undefined
    });

    if (env.ga4.debugMode) {
      console.log('[GA4] Cleared user ID');
    }
  }
};

/**
 * Check if GA4 is initialized and enabled.
 */
export const isGA4Enabled = (): boolean => {
  return initialized && env.ga4.enabled;
};
